import {
  Card,
  Loader,
  ModalComponentWrapper,
  ScrollTrigger,
} from '@/shared/components';
import { useTranslation } from 'react-i18next';
import { useVisitHistoryInfinity } from '../api';
import DisplayIcon from '@/shared/assets/display-icon.svg?react';
import PhoneIcon from '@/shared/assets/phone-icon.svg?react';
import TelegramIcon from '@/shared/assets/telegram-logo.svg?react';
import type { Device } from '../model';
import { format } from 'date-fns';
import { tv } from 'tailwind-variants';
import { DATE_TIME_FORMAT } from '@/shared/config';
import { VisitHistoryEmptyState } from './VisitHistoryEmptyState';

type Props = {
  onBack: () => void;
};

const classes = tv({
  slots: {
    modalHeader: 'text-secondary-bg bg-secondary-bg/50',
    root: 'flex flex-col gap-[1.6rem]',
    content: 'px-[2.4rem] py-[1.6rem] flex flex-col gap-[1.6rem]',
    header: 'flex items-center gap-[1rem]',
    deviceIcon: 'w-[2.4rem] h-[2.4rem]',
    title: 'headline4 text-primary',
    row: 'grid grid-cols-[10rem_1fr] gap-[2.4rem] items-baseline',
    resultRow: 'grid grid-cols-[10rem_1fr] gap-[2.4rem] items-center',
    label: 'p4 text-secondary',
    value: 'headline4 text-primary flex items-center gap-[1rem] break-all',
    resultIcon: 'w-[1.6rem] h-[1.6rem]',
  },
});

export function VisitHistoryModal({ onBack }: Props) {
  const { t } = useTranslation();

  const c = classes();

  const {
    items,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useVisitHistoryInfinity();

  if (isLoading || !items) {
    return <Loader />;
  }

  if (isError) {
    return (
      <ModalComponentWrapper
        title={t('personalAccount.userProfile.visitHistory.title')}
        onBack={onBack}
        headerClassName={c.modalHeader()}>
        <VisitHistoryEmptyState onRefresh={refetch} />
      </ModalComponentWrapper>
    );
  }

  const getDeviceIcon = (device: Device): React.ElementType => {
    const deviceMap: Record<Device, React.ElementType> = {
      Desktop: DisplayIcon,
      Mobile: PhoneIcon,
      'Mobile App': PhoneIcon,
      'Telegram App': TelegramIcon,
    };
    return deviceMap[device] || deviceMap.Desktop;
  };

  const loadMore = () => {
    if (!isFetchingNextPage) fetchNextPage();
  };

  return (
    <ModalComponentWrapper
      title={t('personalAccount.userProfile.visitHistory.title')}
      onBack={onBack}
      headerClassName={c.modalHeader()}>
      <div className={c.root()}>
        {items.map(activity => {
          const DeviceIcon = getDeviceIcon(activity.userDevice);

          return (
            <Card key={activity.loginDateTime} className={c.content()}>
              <div className={c.header()}>
                {DeviceIcon && <DeviceIcon className={c.deviceIcon()} />}
                <span className={c.title()}>{activity.application}</span>
              </div>

              {'failed' in activity && (
                <div className={c.resultRow()}>
                  <div className={c.label()}>
                    {t(
                      'personalAccount.userProfile.visitHistory.loginResult.title'
                    )}
                  </div>
                  <div className={c.value()}>
                    <img
                      src={`/assets/${activity.failed ? 'failed' : 'success'}-status-icon.svg`}
                      alt={activity.failed ? 'Failed' : 'Success'}
                      className={c.resultIcon()}
                    />
                    {t(
                      `personalAccount.userProfile.visitHistory.loginResult.${activity.failed ? 'failed' : 'success'}`
                    )}
                  </div>
                </div>
              )}

              {activity.loginDateTime && (
                <div className={c.row()}>
                  <div className={c.label()}>
                    {t('personalAccount.userProfile.visitHistory.loginDate')}
                  </div>
                  <div className={c.value()}>
                    {format(activity.loginDateTime, DATE_TIME_FORMAT)}
                  </div>
                </div>
              )}

              {activity.location && (
                <div className={c.row()}>
                  <div className={c.label()}>
                    {t('personalAccount.userProfile.visitHistory.location')}
                  </div>
                  <div className={c.value()}>{activity.location}</div>
                </div>
              )}

              {activity.ip && (
                <div className={c.row()}>
                  <div className={c.label()}>
                    {t('personalAccount.userProfile.visitHistory.ipAddress')}
                  </div>
                  <div className={c.value()}>{activity.ip}</div>
                </div>
              )}

              {activity.identityType && (
                <div className={c.row()}>
                  <div className={c.label()}>
                    {t(
                      'personalAccount.userProfile.visitHistory.authorizationMethod'
                    )}
                  </div>
                  <div className={c.value()}>{activity.identityType}</div>
                </div>
              )}
            </Card>
          );
        })}

        {hasNextPage && (
          <ScrollTrigger onEnter={loadMore}>
            <div className="text-center py-4">
              {isFetchingNextPage && <Loader />}
            </div>
          </ScrollTrigger>
        )}
      </div>
    </ModalComponentWrapper>
  );
}
